/**
 * Chain Configurations for Wagmi/WalletConnect
 * Includes all chains supported by Relay Protocol for cross-chain swaps
 */

import type { Chain } from 'wagmi/chains';
import { mainnet, base, optimism, arbitrum, polygon, bsc, avalanche, sepolia } from 'wagmi/chains';

// Ink L2 Chain Configuration
export const ink: Chain = {
  id: 57073,
  name: 'Ink',
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: [
        'https://ink.drpc.org', // DRPC (most reliable for gas estimation)
        'https://ink.rpc.grove.city/v1/01fdb492', // Grove
      ],
    },
    public: {
      http: [
        'https://ink.drpc.org',
      ],
    },
  },
  blockExplorers: {
    default: {
      name: 'Ink Explorer',
      url: 'https://explorer.inkonchain.com',
    },
  },
  contracts: {
    multicall3: {
      address: '0xcA11bde05977b3631167028862bE2a173976CA11',
      blockCreated: 1, // Assuming deployed early or at genesis for OP stack
    },
  },
  testnet: false,
};

// Solana Chain Configuration (Custom - not in wagmi/chains)
export const solana: Chain = {
  id: 101,
  name: 'Solana',
  nativeCurrency: {
    name: 'Solana',
    symbol: 'SOL',
    decimals: 9,
  },
  rpcUrls: {
    default: {
      http: ['https://api.mainnet-beta.solana.com'],
    },
    public: {
      http: ['https://api.mainnet-beta.solana.com'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Solana Explorer',
      url: 'https://explorer.solana.com',
    },
  },
  testnet: false,
};

// Monad Chain Configuration
export const monad: Chain = {
  id: 143,
  name: 'Monad',
  nativeCurrency: {
    name: 'Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://rpc.monad.xyz'],
    },
    public: {
      http: ['https://rpc.monad.xyz'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Monad Explorer',
      url: 'https://www.monadscan.com',
    },
  },
  testnet: false,
};

/**
 * All supported chains for the application
 * These are the chains users can bridge from/to using Relay Protocol
 */
export const ALL_SUPPORTED_CHAINS = [
  ink, // Primary chain - always include
  mainnet, // Ethereum
  sepolia, // Ethereum testnet
  base, // Base
  optimism, // Optimism
  arbitrum, // Arbitrum
  polygon, // Polygon
  bsc, // BNB Chain
  avalanche, // Avalanche
  solana, // Solana
  monad, // Monad
] as const;

/**
 * Helper to get chain by ID
 */
export function getChainById(chainId: number): Chain | undefined {
  return ALL_SUPPORTED_CHAINS.find(chain => chain.id === chainId);
}

/**
 * Note for wagmi configuration:
 * 
 * To enable multi-chain support in your wagmi config, import these chains:
 * 
 * import { mainnet, sepolia, base, optimism, arbitrum, polygon, bsc, avalanche } from 'wagmi/chains';
 * import { ink } from './utils/chains';
 * 
 * Then in your wagmi config (usually in App.tsx or a separate config file):
 * 
 * const config = createConfig({
 *   chains: [ink, mainnet, sepolia, base, optimism, arbitrum, polygon, bsc, avalanche],
 *   transports: {
 *     [ink.id]: http(),
 *     [mainnet.id]: http(),
 *     [sepolia.id]: http(),
 *     [base.id]: http(),
 *     [optimism.id]: http(),
 *     [arbitrum.id]: http(),
 *     [polygon.id]: http(),
 *     [bsc.id]: http(),
 *     [avalanche.id]: http(),
 *   },
 *   // ... other wagmi config
 * });
 * 
 * This allows users to:
 * 1. Switch between chains in their wallet
 * 2. See balances on each chain
 * 3. Sign transactions on any supported chain
 * 4. Better UX for cross-chain operations
 * 
 * However, Relay Protocol works WITHOUT needing to switch chains!
 * Users can stay on any chain and bridge to/from others.
 * Adding chain configs just enhances the UX.
 */

